// dockField — the pure half of NettiDock's pointer field (law_netti_corner).
//
// The dock asks one question of every pointer sample: how much does this person
// mean to talk to netti? Two things answer it, and the answer is the larger of
// the two:
//   • THE CORNER — an elliptic field anchored on the viewport's bottom-right
//     corner. 1 on the mark, 0 at the edge of a 460 × 520 box. The more
//     bottom-right the pointer goes, the clearer netti becomes.
//   • THE COMPOSER — once the rail is up (or rising), the box you would type in
//     is a field of its own, generous to the left and above and open to the
//     right, so a pointer resting anywhere NEAR the text area keeps netti in
//     focus even when it is far from the corner itself.
// Nothing here touches the DOM or a clock: the dock measures the composer once
// per event, hands the rect in, and these functions only do arithmetic. That is
// what keeps the pointer handler free of layout reads, and what the tests pin.

/** netti's corner field, in px from the viewport's bottom-right corner. */
export const FIELD_W = 460;
export const FIELD_H = 520;

/** crossing THE MARK arms the rail (open without a click)… */
export const MARK_ON = 0.72;
/** …and it lets go only well below that, through the leave timer, so a pointer
 *  hovering the threshold (or drifting between the corner and the composer)
 *  can never strobe it. */
export const MARK_OFF = 0.3;
/** a deliberate fold holds the field off until the pointer is out of the mark's
 *  neighbourhood; this is that distance (unchanged from before the composer
 *  field, so a fold feels the same). */
export const SUPPRESS_OFF = 0.46;

/** how far the composer's field reaches, in px: left of its left edge, above its
 *  top edge, below its bottom edge. Right of its left edge is simply inside. */
export const COMPOSER_LEFT = 260;
export const COMPOSER_UP = 180;
export const COMPOSER_DOWN = 100;

/** once armed, the whole near-zone reads as the conversation: clarity is full
 *  from this nearness up and falls away toward the zone's edge. */
export const ARMED_FULL = 0.6;
/** the rail counts as "rising" (so the composer field may speak) above this. */
export const RISING = 0.08;

/** a rect, as the dock caches it from getBoundingClientRect. */
export interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

/** nearness to the bottom-right corner: an elliptic distance in the field box. */
export function cornerProximity(x: number, y: number, w: number, h: number): number {
  const nx = (w - x) / FIELD_W;
  const ny = (h - y) / FIELD_H;
  return clamp01(1 - Math.hypot(nx, ny));
}

/**
 * nearness to the composer: 1 anywhere inside it or to the right of its left
 * edge on its own rows, falling to 0 at COMPOSER_LEFT px to its left or
 * COMPOSER_UP px above it (COMPOSER_DOWN below). Elliptic, so a diagonal
 * approach is a little farther than a straight one. No rect (no composer, a
 * preview rail, a hidden rail) is no field.
 */
export function composerProximity(x: number, y: number, box: Box | null | undefined): number {
  if (!box) return 0;
  const dx = Math.max(0, box.left - x); // right of the left edge counts as inside
  const above = y < box.top;
  const dy = above ? box.top - y : y > box.bottom ? y - box.bottom : 0;
  const ny = dy / (above ? COMPOSER_UP : COMPOSER_DOWN);
  return clamp01(1 - Math.hypot(dx / COMPOSER_LEFT, ny));
}

export interface ProximityIn {
  x: number;
  y: number;
  w: number;
  h: number;
  /** the composer's rect, or null when there is none to measure */
  composer: Box | null;
  /** the rail is up or rising: only then does the composer speak */
  composerLive: boolean;
  /** the pointer is on the rail or on the corner mark itself */
  inside: boolean;
}

/** how much this pointer sample means to talk to netti: the larger of the two fields. */
export function proximity(p: ProximityIn): number {
  if (p.inside) return 1;
  const corner = cornerProximity(p.x, p.y, p.w, p.h);
  const composer = p.composerLive ? composerProximity(p.x, p.y, p.composer) : 0;
  return Math.max(corner, composer);
}

/**
 * the --np target for a pointer-held rail. Unarmed, clarity IS nearness (the
 * corner ramp, unchanged). Armed, the rail is a conversation you can reach, so
 * the near-zone reads in focus (full from ARMED_FULL) and only the zone's edge
 * thins it. It is never below the raw nearness.
 */
export function clarity(prox: number, armed: boolean): number {
  if (!armed) return clamp01(prox);
  return clamp01(prox / ARMED_FULL);
}

/** what the dock reads, LIVE, at the moment a fold is about to happen. */
export interface FoldGuard {
  /** focus is on the composer or anything else in the rail */
  focusedWithin: boolean;
  /** the unsent words in the composer */
  draft: string;
  /** netti is thinking or speaking */
  busy: boolean;
  /** the rail is showing the past, not the conversation */
  view: "chat" | "past";
  /** the netti space holds the rail open */
  pinned: boolean;
}

/**
 * ONE guard for every way the rail can fold by itself (the pointer field letting
 * go, the pointer leaving the rail or the page, the window losing focus, a blur,
 * the leave timer). A fold the person asks for (Esc, the corner mark) never asks.
 */
export function canFold(g: FoldGuard): boolean {
  return !g.pinned && !g.focusedWithin && g.draft.trim().length === 0 && !g.busy && g.view !== "past";
}
